import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { NeedsSetup } from "@/components/NeedsSetup";
import { BidPanel } from "@/components/BidPanel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Briefcase, Plus, Users, CheckCircle, Loader2, Phone, Mail, Building2,
  Globe, Linkedin, Sparkles, Vote, Scale
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PMCSelection = () => {
  const [searchParams] = useSearchParams();
  const societyIdFromUrl = searchParams.get('society');
  
  const [pmcs, setPmcs] = useState([]);
  const [society, setSociety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [voting, setVoting] = useState(null);
  const [voteResults, setVoteResults] = useState(null);
  const [isAdminManaging, setIsAdminManaging] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    contact_person: "",
    phone: "",
    email: "",
    experience_years: "",
    past_projects: "",
    specialization: "",
    fee_structure: "",
    website: "",
    linkedin: ""
  });

  useEffect(() => {
    fetchData();
  }, [societyIdFromUrl]);

  const fetchData = async () => {
    try {
      let societyId;
      
      // If society ID is in URL (admin managing), use that
      if (societyIdFromUrl) {
        const socRes = await fetch(`${API}/societies/${societyIdFromUrl}`, { credentials: "include" });
        if (socRes.ok) {
          const soc = await socRes.json();
          setSociety(soc);
          societyId = societyIdFromUrl;
          setIsAdminManaging(true);
        } else {
          toast.error("Society not found or access denied");
          setLoading(false);
          return;
        }
      } else {
        // Normal flow
        const socRes = await fetch(`${API}/societies`, { credentials: "include" });
        const societies = await socRes.json();
        
        if (societies.length > 0) {
          setSociety(societies[0]);
          societyId = societies[0].society_id;
        }
      }
      
      if (societyId) {
        const [pmcRes, votesRes] = await Promise.all([
          fetch(`${API}/societies/${societyId}/pmc`, { credentials: "include" }),
          fetch(`${API}/societies/${societyId}/votes?entity_type=pmc`, { credentials: "include" })
        ]);
        if (pmcRes.ok) setPmcs(await pmcRes.json());
        if (votesRes.ok) setVoteResults(await votesRes.json());
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (pmcId, voteType = "simple") => {
    if (!society) return;
    setVoting(pmcId);
    try {
      const res = await fetch(`${API}/societies/${society.society_id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entity_type: "pmc",
          entity_id: pmcId,
          vote_type: voteType
        }),
        credentials: "include"
      });

      if (res.ok) {
        toast.success("Vote recorded successfully!");
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to vote");
      }
    } catch (error) {
      toast.error("Failed to vote");
    } finally {
      setVoting(null);
    }
  };

  const getVoteCount = (pmcId) => {
    if (!voteResults) return { simple: 0, weighted: 0 };
    return {
      simple: voteResults.simple_votes?.[pmcId] || 0,
      weighted: voteResults.weighted_votes?.[pmcId] || 0
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddPMC = async (e) => {
    e.preventDefault();
    if (!society) return;

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        company: formData.company,
        contact_person: formData.contact_person || null,
        phone: formData.phone || null,
        email: formData.email || null,
        experience_years: parseInt(formData.experience_years) || 0,
        past_projects: parseInt(formData.past_projects) || 0,
        specialization: formData.specialization || null,
        fee_structure: formData.fee_structure || null
      };

      const res = await fetch(`${API}/societies/${society.society_id}/pmc`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });

      if (res.ok) {
        const newPMC = await res.json();
        setPmcs([...pmcs, newPMC]);
        toast.success("PMC added successfully");
        setDialogOpen(false);
        setFormData({
          name: "", company: "", contact_person: "", phone: "", email: "",
          experience_years: "", past_projects: "", specialization: "", fee_structure: ""
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to add PMC");
      }
    } catch (error) {
      toast.error("Failed to add PMC");
    } finally {
      setSaving(false);
    }
  };

  const updatePMCStatus = async (pmcId, status) => {
    try {
      const res = await fetch(`${API}/societies/${society.society_id}/pmc/${pmcId}/status?status=${status}`, {
        method: "PUT",
        credentials: "include"
      });

      if (res.ok) {
        toast.success(`PMC ${status === 'selected' ? 'selected' : 'status updated'}`);
        fetchData();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      bidding: "bg-blue-100 text-blue-700",
      shortlisted: "bg-amber-100 text-amber-700",
      selected: "bg-emerald-100 text-emerald-700",
      rejected: "bg-red-100 text-red-700"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.bidding}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!society) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <NeedsSetup message="Set up your society to shortlist and manage PMCs." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              PMC Selection
            </h1>
            <p className="text-slate-600">Select Project Management Consultant for redevelopment</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-pmc-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add PMC
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Add PMC Candidate</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddPMC} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company">Company Name *</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="e.g., XYZ Consultants"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="name">Consultant Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., Mr. Amit Joshi"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="e.g., 9876543210"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="e.g., amit@xyz.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="experience_years">Experience (Years)</Label>
                    <Input
                      id="experience_years"
                      name="experience_years"
                      type="number"
                      value={formData.experience_years}
                      onChange={handleChange}
                      placeholder="e.g., 10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="past_projects">Past Projects</Label>
                    <Input
                      id="past_projects"
                      name="past_projects"
                      type="number"
                      value={formData.past_projects}
                      onChange={handleChange}
                      placeholder="e.g., 15"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="specialization">Specialization</Label>
                  <Input
                    id="specialization"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleChange}
                    placeholder="e.g., High-rise residential, SRA projects"
                  />
                </div>
                <div>
                  <Label htmlFor="fee_structure">Fee Structure</Label>
                  <Textarea
                    id="fee_structure"
                    name="fee_structure"
                    value={formData.fee_structure}
                    onChange={handleChange}
                    placeholder="Describe the fee structure..."
                    rows={2}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-700 hover:bg-emerald-800"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add PMC
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Bidding Panel */}
        {society && (
          <BidPanel
            societyId={society.society_id}
            bidType="pmc"
            candidates={pmcs}
            idKey="pmc_id"
            statusPath="pmc"
            onWinnerSelected={fetchData}
          />
        )}

        {/* PMC List */}
        {pmcs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Briefcase className="w-8 h-8 text-emerald-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                No PMC Candidates
              </h3>
              <p className="text-slate-600 max-w-md mx-auto">
                Add Project Management Consultants who can oversee your redevelopment project.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pmcs.map((pmc) => (
              <Card key={pmc.pmc_id} className={`${pmc.status === 'selected' ? 'ring-2 ring-emerald-500' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{pmc.company}</CardTitle>
                      <CardDescription>{pmc.name}</CardDescription>
                    </div>
                    {getStatusBadge(pmc.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-slate-900">{pmc.experience_years || '-'}</p>
                      <p className="text-xs text-slate-500">Years Exp.</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-slate-900">{pmc.past_projects || '-'}</p>
                      <p className="text-xs text-slate-500">Projects</p>
                    </div>
                  </div>

                  {pmc.specialization && (
                    <p className="text-sm text-slate-600">{pmc.specialization}</p>
                  )}

                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    {pmc.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {pmc.phone}
                      </span>
                    )}
                    {pmc.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {pmc.email}
                      </span>
                    )}
                  </div>

                  {/* AI Info */}
                  {pmc.ai_fetched_info && (
                    <div className="p-2 bg-purple-50 rounded text-xs text-purple-700">
                      <Sparkles className="w-3 h-3 inline mr-1" />
                      AI Info: {pmc.ai_fetched_info.substring(0, 120)}...
                    </div>
                  )}

                  {/* Social Links */}
                  {(pmc.website || pmc.linkedin) && (
                    <div className="flex items-center gap-3 pt-2 border-t">
                      {pmc.website && (
                        <a
                          href={pmc.website.startsWith('http') ? pmc.website : `https://${pmc.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Globe className="w-4 h-4" />
                          Website
                        </a>
                      )}
                      {pmc.linkedin && (
                        <a
                          href={pmc.linkedin.startsWith('http') ? pmc.linkedin : `https://${pmc.linkedin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Linkedin className="w-4 h-4" />
                          LinkedIn
                        </a>
                      )}
                    </div>
                  )}

                  {pmc.status !== 'selected' && (
                    <div className="space-y-3 border-t pt-3">
                      {/* Vote Count Display */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 flex items-center gap-1">
                          <Vote className="w-4 h-4" /> Votes
                        </span>
                        <div className="flex gap-3">
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                            Simple: {getVoteCount(pmc.pmc_id).simple}
                          </span>
                          <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-xs">
                            Weighted: {getVoteCount(pmc.pmc_id).weighted.toFixed(1)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Vote Buttons */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleVote(pmc.pmc_id, 'simple')}
                          disabled={voting === pmc.pmc_id}
                          data-testid={`vote-simple-${pmc.pmc_id}`}
                        >
                          {voting === pmc.pmc_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Vote className="w-4 h-4 mr-1" />
                              Vote
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleVote(pmc.pmc_id, 'weighted')}
                          disabled={voting === pmc.pmc_id}
                          data-testid={`vote-weighted-${pmc.pmc_id}`}
                        >
                          <Scale className="w-4 h-4 mr-1" />
                          Weighted
                        </Button>
                      </div>
                      
                      {/* Status Buttons */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => updatePMCStatus(pmc.pmc_id, 'shortlisted')}
                        >
                          Shortlist
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                          onClick={() => updatePMCStatus(pmc.pmc_id, 'selected')}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Select
                        </Button>
                      </div>
                    </div>
                  )}

                  {pmc.status === 'selected' && (
                    <div className="p-3 bg-emerald-50 rounded-lg text-center">
                      <CheckCircle className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                      <p className="text-sm font-medium text-emerald-700">Selected PMC</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
